import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  Download,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  FolderPlus,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, apiError, fmtWhen } from "@/lib/api";

const ICONS = {
  pdf: FileText,
  docx: FileText,
  csv: FileSpreadsheet,
  jpg: ImageIcon,
  jpeg: ImageIcon,
  png: ImageIcon,
};

const size = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1048576).toFixed(bytes > 10485760 ? 0 : 1)} MB`;
};

/**
 * Important Files — the portal's own shelf.
 *
 * Every signed-in officer may open a folder and take a file out. Making a folder, bringing a file
 * in and taking one off belong to SDTD administrators and the portal's super-admins. A file goes
 * up in pieces, so a 250 MB pack of drawings gets through in one go.
 */
export default function Library() {
  const [folder, setFolder] = useState("");
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [ask, setAsk] = useState(null);
  const picker = useRef(null);

  const load = useCallback(() => {
    api
      .get("/library", { params: { folder } })
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        setFolder("");
      });
  }, [folder]);

  useEffect(load, [load]);

  const makeFolder = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/library/folders", { name: name.trim(), parent_id: folder });
      toast.success(`"${name.trim()}" is ready.`);
      setName("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (chosen) => {
    const files = Array.from(chosen || []);
    if (!files.length) return;
    const limits = data.limits;
    for (let n = 0; n < files.length; n += 1) {
      const file = files[n];
      if (file.size > limits.max_bytes) {
        toast.error(`"${file.name}" is ${size(file.size)}. One file may be up to 250 MB.`);
        continue;
      }
      const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
      if (!limits.kinds.includes(ext)) {
        toast.error(`The shelf takes ${limits.kinds.join(", ")} files.`);
        continue;
      }
      setJob({ name: file.name, done: 0, of: 1, file: n + 1, files: files.length });
      try {
        const chunkBytes = limits.chunk_bytes;
        const chunks = Math.max(1, Math.ceil(file.size / chunkBytes));
        // eslint-disable-next-line no-await-in-loop
        const { data: started } = await api.post("/library/uploads", {
          filename: file.name,
          size: file.size,
          content_type: file.type || "",
          folder_id: folder,
          chunks,
        });
        setJob({ name: file.name, done: 0, of: chunks, file: n + 1, files: files.length });
        for (let i = 0; i < chunks; i += 1) {
          const piece = file.slice(i * chunkBytes, Math.min(file.size, (i + 1) * chunkBytes));
          const form = new FormData();
          form.append("chunk", piece, `${file.name}.part${i}`);
          // eslint-disable-next-line no-await-in-loop
          await api.put(`/library/uploads/${started.upload_id}/chunk?index=${i}`, form);
          setJob({ name: file.name, done: i + 1, of: chunks, file: n + 1, files: files.length });
        }
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/library/uploads/${started.upload_id}/finish`);
        toast.success(`"${file.name}" is on the shelf.`);
      } catch (err) {
        toast.error(apiError(err));
      }
    }
    setJob(null);
    if (picker.current) picker.current.value = "";
    load();
  };

  const download = async (file) => {
    setBusy(true);
    try {
      const res = await api.get(`/library/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      if (ask.kind === "folder") await api.delete(`/library/folders/${ask.id}`);
      else await api.delete(`/library/files/${ask.id}`);
      toast.success(`"${ask.name}" is off the shelf.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      setAsk(null);
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="library-page">
      <header>
        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
          Important Files
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          The portal&apos;s own shelf — circulars, formats, approvals and packs, in folders of your
          making. Everybody signed in may open and take out what is here; SDTD administrators and
          the portal&apos;s super-admins put files on it.
        </p>
        <p className="font-data mt-2 text-[11px] text-muted-foreground" data-testid="library-totals">
          {data.totals.files} file(s) · {size(data.totals.bytes)} · {data.totals.folders} folder(s)
          · {data.limits.kinds.join(", ").toUpperCase()} up to 250 MB each
        </p>
      </header>

      <nav className="flex flex-wrap items-center gap-1 text-xs" data-testid="library-breadcrumb">
        <button
          type="button"
          data-testid="library-crumb-root"
          onClick={() => setFolder("")}
          className={`font-display font-bold uppercase tracking-widest transition-colors duration-200 hover:text-primary ${
            folder ? "text-muted-foreground" : "text-foreground"
          }`}
        >
          All files
        </button>
        {data.path.map((crumb, i) => (
          <span key={crumb.id} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <button
              type="button"
              data-testid={`library-crumb-${crumb.id}`}
              onClick={() => setFolder(crumb.id)}
              className={`font-display font-bold uppercase tracking-widest transition-colors duration-200 hover:text-primary ${
                i === data.path.length - 1 ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </nav>

      {data.can_write && (
        <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4">
          <form onSubmit={makeFolder} className="flex items-end gap-2">
            <label className="block">
              <span className="label-caps block">A new folder here</span>
              <Input
                className="mt-1 h-11 w-[240px] rounded-none"
                placeholder="Ministry formats"
                data-testid="library-folder-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button
              type="submit"
              disabled={busy || !name.trim()}
              data-testid="library-folder-add"
              className="flex h-11 items-center gap-2 border border-border px-4 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-40"
            >
              <FolderPlus className="h-4 w-4" /> Make it
            </button>
          </form>

          <div className="flex items-end gap-2">
            <input
              ref={picker}
              type="file"
              multiple
              className="hidden"
              data-testid="library-file-input"
              accept=".pdf,.docx,.jpg,.jpeg,.png,.csv"
              onChange={(e) => upload(e.target.files)}
            />
            <button
              type="button"
              disabled={!!job}
              data-testid="library-upload"
              onClick={() => picker.current?.click()}
              className="flex h-11 items-center gap-2 bg-primary px-5 font-display text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
            >
              {job ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Bring files in
            </button>
          </div>

          {job && (
            <div className="min-w-[220px]" data-testid="library-upload-progress">
              <div className="font-data flex justify-between text-[10px]">
                <span className="truncate">{job.name}</span>
                <span>{Math.round((job.done / job.of) * 100)}%</span>
              </div>
              <div className="mt-1 h-1.5 w-full bg-muted">
                <div
                  className="h-1.5 bg-primary transition-[width] duration-300"
                  style={{ width: `${Math.round((job.done / job.of) * 100)}%` }}
                />
              </div>
              <span className="font-data text-[10px] text-muted-foreground">
                piece {job.done} of {job.of}
                {job.files > 1 ? ` · file ${job.file} of ${job.files}` : ""}
              </span>
            </div>
          )}
        </div>
      )}

      {!!data.folders.length && (
        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {data.folders.map((f) => (
            <div
              key={f.id}
              className="group flex items-center gap-3 bg-card p-4"
              data-testid={`library-folder-${f.id}`}
            >
              <button
                type="button"
                onClick={() => setFolder(f.id)}
                data-testid={`library-open-${f.id}`}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <FolderOpen className="h-5 w-5 shrink-0 text-primary" />
                <span className="min-w-0">
                  <span className="block truncate font-display text-sm font-bold tracking-tight">
                    {f.name}
                  </span>
                  <span className="font-data block text-[10px] text-muted-foreground">
                    {f.files} file(s) · {f.folders} folder(s)
                    {f.made_by ? ` · made by ${f.made_by}` : ""}
                  </span>
                </span>
              </button>
              {data.can_write && (
                <button
                  type="button"
                  data-testid={`library-folder-remove-${f.id}`}
                  onClick={() => setAsk({ kind: "folder", id: f.id, name: f.name })}
                  className="text-muted-foreground opacity-0 transition-opacity duration-200 hover:text-[hsl(var(--critical))] group-hover:opacity-100"
                  title="Remove this folder"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="border border-border bg-card">
        <div className="label-caps border-b border-border px-4 py-3">
          Files in {data.path.length ? data.path[data.path.length - 1].name : "All files"}
        </div>
        <div className="divide-y divide-border">
          {data.files.map((file) => {
            const Icon = ICONS[file.kind] || FileIcon;
            return (
              <div
                key={file.id}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
                data-testid={`library-file-${file.id}`}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{file.name}</span>
                  <span className="font-data block text-[10px] text-muted-foreground">
                    {file.kind.toUpperCase()} · {size(file.size)}
                    {file.added_by ? ` · brought in by ${file.added_by}` : ""}
                    {file.added_at ? ` · ${fmtWhen(file.added_at)}` : ""}
                    {file.downloads ? ` · taken out ${file.downloads} time(s)` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={busy}
                  data-testid={`library-download-${file.id}`}
                  onClick={() => download(file)}
                  className="flex items-center gap-1 border border-border px-2 py-1.5 font-display text-[9.5px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-40"
                >
                  <Download className="h-3 w-3" /> Take it out
                </button>
                {data.can_write && (
                  <button
                    type="button"
                    data-testid={`library-file-remove-${file.id}`}
                    onClick={() => setAsk({ kind: "file", id: file.id, name: file.name })}
                    className="text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--critical))]"
                    title="Take this file off the shelf"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
          {!data.files.length && (
            <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="library-empty">
              Nothing here yet.
              {data.can_write
                ? " Make a folder, or bring the first file in."
                : " SDTD will put the circulars and formats here."}
            </p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!ask}
        onOpenChange={(v) => !v && setAsk(null)}
        title={`Take "${ask?.name || ""}" off the shelf?`}
        description={
          ask?.kind === "folder"
            ? "A folder is removed only when nothing is inside it."
            : "It stops being served from this moment. The activity trail keeps who removed it and when."
        }
        confirmLabel="Take it off"
        tone="danger"
        testId="confirm-library-remove"
        onConfirm={remove}
      />
    </div>
  );
}
